import { useEffect, useState } from 'react'
import { CheckCircle2, Clock, Inbox, XCircle } from 'lucide-react'
import { api } from '../../api/client'
import type { MailboxRequest } from '../../api/types'
import { EmptyState } from '../../components/EmptyState'
import { PageHeader } from '../../components/PageHeader'
import { useI18n } from '../../i18n'

export function MailboxRequestsPage() {
  const { t } = useI18n()
  const [items, setItems] = useState<MailboxRequest[]>([])
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [filter, setFilter] = useState<'pending' | 'approved' | 'rejected' | ''>('pending')
  const [actionId, setActionId] = useState<string | null>(null)
  const [rejectNote, setRejectNote] = useState('')
  const [showRejectModal, setShowRejectModal] = useState<string | null>(null)

  const load = async () => {
    try {
      const data = await api.get<MailboxRequest[]>(`/admin/mailbox-requests${filter ? `?status=${filter}` : ''}`)
      setItems(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load')
    }
  }

  useEffect(() => { void load() }, [filter])

  async function approve(id: string) {
    setActionId(id)
    setError('')
    try {
      await api.post(`/admin/mailbox-requests/${id}/approve`, { note: '' })
      setItems(prev => prev.filter(i => i.id !== id))
      setSuccess(t('admin.requestApproved'))
      setTimeout(() => setSuccess(''), 3000)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to approve')
    } finally {
      setActionId(null)
    }
  }

  async function reject(id: string) {
    setActionId(id)
    setError('')
    try {
      await api.post(`/admin/mailbox-requests/${id}/reject`, { note: rejectNote })
      setItems(prev => prev.filter(i => i.id !== id))
      setShowRejectModal(null)
      setRejectNote('')
      setSuccess(t('admin.requestRejected'))
      setTimeout(() => setSuccess(''), 3000)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reject')
    } finally {
      setActionId(null)
    }
  }

  const statusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock size={14} className="text-amber-500" />
      case 'approved': return <CheckCircle2 size={14} className="text-emerald-500" />
      case 'rejected': return <XCircle size={14} className="text-red-500" />
      default: return null
    }
  }

  return <>
    <PageHeader title="admin.mailboxRequests" />

    {error && <p className="mb-4 rounded-lg bg-destructive/10 px-4 py-2.5 text-sm text-destructive">{error}</p>}
    {success && <p className="mb-4 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-sm text-emerald-700">{success}</p>}

    <div className="mb-4 flex gap-2">
      {(['pending', 'approved', 'rejected', ''] as const).map(s => (
        <button
          key={s}
          onClick={() => setFilter(s)}
          className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${filter === s ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted/50'}`}
        >
          {s === '' ? t('common.all') : t(`admin.status_${s}`)}
        </button>
      ))}
    </div>

    {items.length === 0 ? (
      <EmptyState title="admin.emptyMailboxRequests" />
    ) : (
      <div className="space-y-3">
        {items.map(item => (
          <div key={item.id} className="panel p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                {statusIcon(item.status)}
                <div>
                  <p className="text-sm font-medium">{item.prefix}@{item.domainName}</p>
                  <p className="text-xs text-muted-foreground">{t('admin.requestedBy')}: {item.username} · {new Date(item.createdAt).toLocaleString()}</p>
                </div>
              </div>
              {item.status === 'pending' && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => approve(item.id)}
                    disabled={actionId === item.id}
                    className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 transition-colors"
                  >
                    {t('admin.approveRequest')}
                  </button>
                  <button
                    onClick={() => { setShowRejectModal(item.id); setRejectNote('') }}
                    disabled={actionId === item.id}
                    className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 transition-colors"
                  >
                    {t('admin.rejectRequest')}
                  </button>
                </div>
              )}
            </div>
            {item.adminNote && item.status !== 'pending' && (
              <p className="text-xs text-muted-foreground mt-2 pl-7">{t('admin.adminNote')}: {item.adminNote}</p>
            )}
          </div>
        ))}
      </div>
    )}

    {showRejectModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/40" onClick={() => setShowRejectModal(null)} />
        <div className="relative bg-card rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
          <h3 className="text-base font-semibold mb-3">{t('admin.rejectConfirm')}</h3>
          <textarea
            className="field w-full h-24 resize-none"
            value={rejectNote}
            onChange={e => setRejectNote(e.target.value)}
            placeholder={t('admin.rejectReason')}
          />
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setShowRejectModal(null)} className="btn-secondary">{t('common.cancel')}</button>
            <button
              onClick={() => reject(showRejectModal)}
              disabled={actionId !== null}
              className="rounded-lg bg-red-600 text-white px-4 py-2 text-sm font-medium hover:bg-red-700 transition-colors"
            >
              {t('admin.rejectRequest')}
            </button>
          </div>
        </div>
      </div>
    )}
  </>
}
